import React, { JSX } from "react";
import FunctionsIcon from "@mui/icons-material/Functions";
import { StatusChip } from "components/common/StatusChip";
import { Inline } from "components/common/Stack";
import type { RecipeListItem } from "components/canvas/recipes/recipesApi";

interface RecipeCatalogRowProps {
  recipe: RecipeListItem;
  onOpen: () => void;
}

/**
 * One catalog row, opened by clicking anywhere on it.
 *
 * No hover icon: nothing a recipe supports is both frequent and reversible, and
 * the destructive act lives inside the overlay behind its own confirmation.
 * The Σ is the same mark a result carries on the model graph and in the import
 * panel.
 */
export function RecipeCatalogRow({
  recipe,
  onOpen,
}: RecipeCatalogRowProps): JSX.Element {
  return (
    <div
      // No focus style, matching `Button`: closing the overlay with Esc returns
      // focus here and it stays, so anything drawn persists and reads as a
      // selection on a full-width row.
      className="group flex cursor-pointer select-none items-center gap-2 rounded-app px-1 py-1.5 transition-colors hover:bg-bg-hover focus:outline-none"
      tabIndex={0}
      role="button"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <span className="flex size-6 shrink-0 items-center justify-center text-fg-muted">
        <FunctionsIcon className="icon-sm" />
      </span>

      <div className="min-w-0 flex-1">
        <Inline gap="xs" className="min-w-0 items-center">
          <span
            className={`truncate ${recipe.deprecated ? "text-fg-muted" : "text-fg"}`}
            style={{
              fontSize: "var(--font-size)",
              fontWeight: "var(--font-weight-medium)",
            }}
          >
            {recipe.name}
          </span>
          {recipe.state === "published" && (
            <StatusChip tone="accent">published</StatusChip>
          )}
          {recipe.deprecated && (
            <StatusChip tone="warning">deprecated</StatusChip>
          )}
        </Inline>
        {recipe.description && (
          <span className="block truncate leading-tight text-fg-muted-regular-small">
            {recipe.description}
          </span>
        )}
      </div>

      {/* Only where it tells the reader something: under "Yours" every row
          would say the same word. */}
      {!recipe.mine && (
        <span className="shrink-0 text-small text-fg-muted">
          {recipe.author.name ?? "—"}
        </span>
      )}
    </div>
  );
}
